import Seo from "@/components/feature/Seo";
import PageHero from "@/components/feature/PageHero";
import Reveal from "@/components/base/Reveal";
import SectionHeading from "@/components/base/SectionHeading";
import ActionButton from "@/components/base/ActionButton";
import { solutions } from "@/mocks/solutions";

export default function Solutions() {
  return (
    <>
      <Seo
        title="EV Retrofit Solutions | Passenger, Commercial & Fleet"
        description="EV retrofit solutions for 2-wheelers, 3-wheelers, passenger and commercial vehicles, cargo, fleets and special applications. Feasibility subject to assessment."
        keywords="EV conversion solutions, commercial vehicle retrofit, fleet retrofit, 3-wheeler retrofit"
        path="/solutions"
      />
      <PageHero
        eyebrow="Solutions"
        breadcrumb="Solutions"
        title="Retrofit Solutions Across Vehicle Categories"
        description="Solutions are organised by vehicle type and application. Each one follows the same principle: understand the vehicle, assess feasibility, then engineer the retrofit."
      >
        <ActionButton to="/ev-retrofit#eligibility" variant="primary" size="md" icon="ri-flashlight-line">
          Check Your Vehicle
        </ActionButton>
      </PageHero>

      <section className="w-full bg-background-50 py-16 md:py-24">
        <div className="mx-auto w-full max-w-container px-4 md:px-6">
          <Reveal>
            <SectionHeading
              eyebrow="Solution Categories"
              title="Problem, Retrofit Approach and Benefits"
              description="Each solution describes the problem it addresses, how retrofit is approached, and the expected benefits. No numerical performance claims are made — those depend on the specific vehicle and configuration."
            />
          </Reveal>

          <div className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-2">
            {solutions.map((item, index) => (
              <Reveal key={item.key} delay={index * 0.06}>
                <article className="flex h-full flex-col rounded-lg border border-background-200 bg-background-100 p-6 md:p-8">
                  <div className="flex items-center gap-4">
                    <span className="flex h-12 w-12 items-center justify-center rounded-md bg-foreground-950 text-primary-400">
                      <i className={`${item.icon} text-2xl`} />
                    </span>
                    <h3 className="text-xl font-bold text-foreground-950">{item.title}</h3>
                  </div>

                  <div className="mt-6 space-y-5">
                    <div>
                      <p className="font-label text-[11px] font-bold uppercase tracking-[0.16em] text-foreground-500">
                        Problem
                      </p>
                      <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{item.problem}</p>
                    </div>
                    <div>
                      <p className="font-label text-[11px] font-bold uppercase tracking-[0.16em] text-foreground-500">
                        Retrofit Approach
                      </p>
                      <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{item.approach}</p>
                    </div>
                    <div>
                      <p className="font-label text-[11px] font-bold uppercase tracking-[0.16em] text-foreground-500">
                        Benefits
                      </p>
                      <ul className="mt-2 space-y-2">
                        {item.benefits.map((benefit) => (
                          <li key={benefit} className="flex items-start gap-2.5 text-sm text-foreground-700">
                            <i className="ri-check-line mt-0.5 text-primary-600" />
                            {benefit}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="mt-6 border-t border-background-200 pt-6">
                    <ActionButton to="/ev-retrofit#eligibility" variant="dark" size="sm" iconRight="ri-arrow-right-line">
                      Check This Category
                    </ActionButton>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full bg-background-100 py-16 md:py-24">
        <div className="mx-auto w-full max-w-container px-4 md:px-6">
          <Reveal>
            <div className="rounded-lg border border-primary-200 bg-primary-50 p-6 md:p-10">
              <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
                <SectionHeading
                  eyebrow="Important"
                  title="Feasibility Is Always Assessed First"
                  description="A solution category describes where retrofit can apply — it does not guarantee that a specific vehicle qualifies. Final feasibility depends on vehicle condition, architecture, weight, usage and engineering assessment."
                />
                <div className="shrink-0">
                  <ActionButton to="/ev-retrofit#eligibility" variant="primary" size="lg" icon="ri-search-eye-line">
                    Start Your Assessment
                  </ActionButton>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}